// src/services/api/orders.js
import { apiClient } from './base';

export const createEcomOrder = async (orderData) => {
  try {
    const response = await apiClient.post('/ecom-orders', orderData, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEcomOrders = async (params = {}, { signal } = {}) => {
  try {
    const response = await apiClient.get('/ecom-orders', { params, signal });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch a paginated batch of orders for export.
 * The frontend calls this repeatedly (page 1, 2, 3…) to assemble the full dataset.
 * Server caps `limit` at 1000 per request.
 *
 * @param {Object} params - Same filter params as getEcomOrders, plus page & limit.
 * @returns {Promise<{ total: number, orders: Array }>}
 */
export const exportEcomOrders = async (params = {}) => {
  try {
    const response = await apiClient.get('/ecom-orders/export', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEcomOrder = async (orderId) => {
  try {
    const response = await apiClient.get(`/ecom-orders/${orderId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateEcomOrder = async (orderId, orderData) => {
  try {
    const response = await apiClient.put(`/ecom-orders/${orderId}`, orderData, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateInCourierAdjustments = async (orderId, data) => {
  try {
    const response = await apiClient.patch(`/ecom-orders/${orderId}/in-courier-adjustments`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrderAuditHistory = async (orderId, params = {}) => {
  try {
    const response = await apiClient.get(`/ecom-orders/${orderId}/audit`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrderAuditSummary = async (orderId) => {
  try {
    const response = await apiClient.get(`/ecom-orders/${orderId}/audit/summary`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkCreateOrders = async (
  shopId,
  orders,
  dryRun = false,
  skipAutoAssign = false,
  duplicateCheckDays = 2,
  messageCenterShiftId = null
) => {
  try {
    const response = await apiClient.post('/ecom-orders/bulk', {
      shopId,
      orders,
      dryRun,
      skipAutoAssign,
      duplicateCheckDays,
      messageCenterShiftId
    }, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkUpdateOrderStatuses = async (data) => {
  try {
    const response = await apiClient.put('/ecom-orders/bulk-update', data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrderCourierTracking = async (orderId, params = {}) => {
  try {
    const response = await apiClient.get(`/ecom-orders/${orderId}/courier-tracking`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrderRelations = async (orderId) => {
  try {
    const response = await apiClient.get(`/ecom-orders/${orderId}/relations`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get order status distribution
 * @param {Object} params - Query parameters
 * @param {string|number} params.shopId - Shop ID or 'all'
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Order count and value breakdown by status
 */
export const getOrderStatusDistribution = async (params = {}) => {
  try {
    const response = await apiClient.get('/ecom-orders/status-distribution', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Lookup order by order number or airwaybill number
 * @param {string} identifier - Order number or airwaybill number
 * @param {number|null} excludeReplacementOrderId - When editing a replacement order, its own id,
 *   so its own already-claimed return quantities aren't double-subtracted from "remaining"
 * @returns {Promise<Object>} Order details
 */
export const lookupOrder = async (identifier, excludeReplacementOrderId = null) => {
  try {
    const query = excludeReplacementOrderId ? `?excludeReplacementOrderId=${excludeReplacementOrderId}` : '';
    const response = await apiClient.get(`/ecom-orders/lookup/${identifier}${query}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const phoneLookupByPhone = async (phone) => {
  try {
    const response = await apiClient.get(`/ecom-orders/phone-lookup/${encodeURIComponent(phone)}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDeliveryFollowUpNote = async (orderId, noteData) => {
  try {
    const response = await apiClient.post(`/ecom-orders/${orderId}/delivery-follow-up-notes`, noteData, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDeliveryFollowUpNotes = async (orderId) => {
  try {
    const response = await apiClient.get(`/ecom-orders/${orderId}/delivery-follow-up-notes`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDeliveryFollowUpNoteCounts = async (orderIds = []) => {
  try {
    const response = await apiClient.get('/ecom-orders/delivery-follow-up-notes/counts', {
      params: { orderIds: orderIds.join(',') }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadOrderPaymentProof = async (file) => {
  try {
    const formData = new FormData();
    formData.append('paymentProof', file);
    const response = await apiClient.post('/ecom-orders/payment-proof', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.proofUrl;
  } catch (error) {
    throw error;
  }
};